import { MeasurementType } from "../../shared/enums/Result";
import { UTP2EventHeadings } from "../../shared/enums/UTP2";
import { UTP2Events } from "../../shared/interfaces/UTP2";
import { getHeadingIndices, PlotValue } from "./PhaseResolvedFunctions";

/**
 * Shifts the amplitude value to reduce resolution for Ultrasonic measurements.
 * This amplitude shift is also being done in the UTP2 result javascript for Ultrasonic measurements - see below comment from there for explanation:
 * "Amplitude is floating point and has too much resolution, so round amplitude to 1/3 dB so that there is some grouping to show as hotspots on the colour map."
 **/
function shiftUltrasonicAmplitude(value: number) {
  return Math.round(value * 3) / 3;
}

export function buildPRPSPlots(
  events: UTP2Events,
  measurementType: MeasurementType,
  riseTime?: [number, number],
  pulseWidth?: [number, number],
  phaseShift: number = 0,
) {
  if (events.headings == null) return [];
  const headingIndices = getHeadingIndices(events.headings, measurementType);
  const cycleNumberOffset =
    events?.data[0][events.headings.findIndex((heading) => heading === UTP2EventHeadings.CycleNumber)];
  return events?.data.reduce((accumulator: PlotValue[][], current) => {
    const xValue = (current[headingIndices.x] + 360 + phaseShift) % 360;
    let yValue = current[headingIndices.y];
    let cycleNumber = current[events.headings.findIndex((heading) => heading === UTP2EventHeadings.CycleNumber)];
    if (cycleNumber < cycleNumberOffset)
      // add 16bit integer max onto value. See T15711 for details
      cycleNumber += 65536;
    const zValue = cycleNumber - cycleNumberOffset;
    const risetime = current[events.headings.findIndex((heading) => heading === UTP2EventHeadings.RiseTime)];
    const width = current[events.headings.findIndex((heading) => heading === UTP2EventHeadings.PulseWidth)];

    if (accumulator[zValue] == null) {
      accumulator[zValue] = [];
    }

    if (
      (riseTime !== undefined && (risetime < riseTime[0] || risetime > riseTime[1])) ||
      (pulseWidth !== undefined && (width < pulseWidth[0] || width > pulseWidth[1]))
    ) {
      return accumulator;
    }

    const foundValue = accumulator[zValue].find((record) => record.x === xValue);

    if (measurementType === MeasurementType.Ultra) {
      yValue = shiftUltrasonicAmplitude(yValue);
    } else if (measurementType === MeasurementType.VPIS) {
      yValue = Math.abs(Math.round(yValue));
    } else if (measurementType === MeasurementType.CablePD) {
      yValue = Math.abs(yValue);
    }

    // update the already found x,z coordinates
    if (foundValue) {
      foundValue.y = Math.max(foundValue.y, yValue);
    } else {
      accumulator[zValue].push({ x: xValue, y: yValue, z: zValue });
    }
    return accumulator;
  }, []);
}
